import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { company_name: "", contact_name: "", email: "", phone: "", address: "", vat_id: "", payment_terms_days: 14, notes: "" };

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/customers")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.patch(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success(editing ? "Kunde aktualisiert" : "Kunde angelegt");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Kunde wirklich löschen?")) return;
    await api.delete(`/customers/${id}`); toast.success("Gelöscht"); load();
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Kunden</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Kundenstamm</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button data-testid="add-customer-btn"><Plus className="h-4 w-4 mr-2" />Neuer Kunde</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Kunde bearbeiten" : "Neuer Kunde"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Firmenname</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="customer-company" /></div>
              <div><Label>Ansprechpartner</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div><Label>E-Mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Zahlungsziel (Tage)</Label><Input type="number" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: Number(e.target.value) })} /></div>
              <div className="col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="col-span-2"><Label>USt-ID (optional)</Label><Input value={form.vat_id} onChange={(e) => setForm({ ...form, vat_id: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notizen</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="customer-save">{editing ? "Speichern" : "Anlegen"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white">
        <Table data-testid="customers-table">
          <TableHeader>
            <TableRow>
              <TableHead>Firma</TableHead><TableHead>Ansprechpartner</TableHead><TableHead>E-Mail</TableHead><TableHead>Zahlungsziel</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} className="table-row-hover" data-testid={`customer-row-${c.id}`}>
                <TableCell className="font-medium">{c.company_name}</TableCell>
                <TableCell>{c.contact_name}</TableCell>
                <TableCell className="text-neutral-600 text-sm">{c.email}</TableCell>
                <TableCell>{c.payment_terms_days} Tage</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setForm(c); setOpen(true); }} data-testid={`customer-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(c.id)} data-testid={`customer-delete-${c.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-12">Noch keine Kunden</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
